import ProfileIcon, {
    ProfileMenuItem,
} from "@/Components/Navigation/ProfileIcon";
import ApplicationLogo from "@/Components/UI/Branding/ApplicationLogo";
import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import { isVideoShowingAtom } from "@/Components/VideoRooms/VideoParts/videoAtoms";
import { GiftCoachingContext } from "@/Contexts/GiftCoachingContext";
import { Client, Coach, DecoyClient } from "@/types/page";
import classNames from "@/utils/classNames";
import { logout, useIdentity } from "@/utils/identity";
import { Link, navigateTo } from "@/utils/navigation";
import { useCoachApplicationPageExperiment } from "@/utils/useCoachApplicationPageExperiment";
import { Disclosure } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { ReactElement, useContext, useEffect, useState } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { FiGift } from "react-icons/fi";
import { HiOutlineVideoCamera } from "react-icons/hi";
import { LuHouse, LuMessageSquareMore, LuSettings } from "react-icons/lu";
import { TbLogout } from "react-icons/tb";
import { RouteName } from "ziggy-js";

type NavigationItem =
    | {
          name: string;
          href: string;
          current: boolean;
      }
    | {
          name: string;
          customComponent: ReactElement;
      };

export type NavbarDefinition = {
    navigation: NavigationItem[];
    profileMenuItems: ProfileMenuItem[];
};

type NavbarProps = {
    user: Client | Coach | DecoyClient | null;
    backgroundColor?: string;
    useStrawberryLogo?: boolean;
    transparentAtTop?: boolean;
};

const SIMPLE_NAVBAR_ROUTES: RouteName[] = [
    "quiz",
    "start",
    "video-splash",
    "switch-page.index",
    "client.register",
    "client.register.phone-number",
    "client.register.verify-phone-page",
    "verification.notice",
    "client.paymentMethod.view",
    "client.paymentMethod",
    "resume-coaching",
    "winback-offer",
    "pick-new-coach",
    "pick-new-coach.checkout",
    "client.pairingInProgress",
    "client.coachSelection",
    "client.postPaymentCoachPick",
    "second-bite.view-coaches",
    "second-bite.payment",
    "say-more",
    "personalized-intake-quiz.index",
    "client.intakeQuizSummary",
];

const SIMPLE_NAVBAR_ROUTES_WITH_ACCOUNT_LINK: RouteName[] = ["pick-new-coach"];

const LOGO_CLICK_DISALLOWED_ROUTES: RouteName[] = [
    "start",
    "second-bite.view-coaches",
    "second-bite.payment",
];

export default function Navbar({
    user,
    backgroundColor,
    useStrawberryLogo,
    transparentAtTop = false,
}: NavbarProps) {
    const { isImpersonating } = useIdentity();

    const { showGiftCoachingModal, canDoReferrals } =
        useContext(GiftCoachingContext);

    const { openCoachApplyQuiz } = useCoachApplicationPageExperiment();

    const isMinimized = useAtomValue(isVideoShowingAtom);

    const [isAtTop, setIsAtTop] = useState(window.scrollY < 50);

    const showTransparent = transparentAtTop && isAtTop && !isImpersonating;

    const navigation: NavbarDefinition["navigation"] = [];
    let profileMenuItems: NavbarDefinition["profileMenuItems"] = [];

    // An expired verification link lands on verification.notice too, but it rendered with the full navbar.
    const isExpiredVerificationLink = new URLSearchParams(
        window.location.search,
    ).has("expired");
    const isSimpleNavbar =
        SIMPLE_NAVBAR_ROUTES.includes(route().current() ?? "") &&
        !isExpiredVerificationLink;

    if (!user) {
        if (!isSimpleNavbar) {
            navigation.push({
                name: "FAQ",
                href: route("faq"),
                current: route().current("faq"),
            });
            navigation.push({
                name: "Contact Us",
                href: route("contact"),
                current: route().current("contact"),
            });
            navigation.push({
                name: "Apply as a Coach",
                customComponent: (
                    <button
                        key="Apply as a Coach"
                        onClick={() => openCoachApplyQuiz()}
                        className={classNames(
                            "rounded-md px-3 py-2 text-base lg:text-sm font-medium hover:bg-brand-red hover:text-brand-white active:bg-brand-primaryButtonClick transition-colors duration-300",
                            showTransparent
                                ? "lg:not-last:text-brand-white"
                                : "text-brand-black",
                        )}
                    >
                        Apply as a Coach
                    </button>
                ),
            });
        }
        navigation.push({
            name: "Log in",
            href: route("client.login"),
            current: route().current("client.login"),
        });
        if (!route().current("start") && !route().current("quiz")) {
            navigation.push({
                name: "Select a Coach",
                customComponent: (
                    <PrimaryButton
                        key="Start Coaching"
                        onClick={() => void navigateTo(route("quiz"))}
                    >
                        Select a Coach
                    </PrimaryButton>
                ),
            });
        }
    } else if (user.type === "client") {
        if (
            isSimpleNavbar ||
            (route().current()?.startsWith("switch-page") &&
                !user.has_payment_setup)
        ) {
            profileMenuItems = SIMPLE_NAVBAR_ROUTES_WITH_ACCOUNT_LINK.includes(
                route().current() ?? "",
            )
                ? [
                      {
                          name: "Account",
                          href: route("client.profile.edit"),
                          icon: <LuSettings />,
                      },
                      {
                          name: "Log out",
                          onClick: () => void logout(),
                          icon: <TbLogout />,
                      },
                  ]
                : [
                      {
                          name: "Log out",
                          onClick: () => void logout(),
                      },
                  ];
        } else {
            profileMenuItems = [
                {
                    name: "Home",
                    selected: route().current("chat"),
                    href: route("chat"),
                    icon: <LuHouse />,
                },
                ...(canDoReferrals
                    ? [
                          {
                              name: "Gift a free trial",
                              icon: <FiGift />,
                              onClick: showGiftCoachingModal,
                          },
                      ]
                    : []),
                {
                    name: "FAQ",
                    selected: route().current("faq"),
                    href: route("faq"),
                    icon: <AiOutlineQuestionCircle />,
                },
                {
                    name: "Contact Us",
                    selected: route().current("client.contact-us.show"),
                    href: route("client.contact-us.show"),
                    icon: <LuMessageSquareMore />,
                },
                {
                    name: "Video and Audio Test",
                    selected: route().current("video-test"),
                    href: route("video-test"),
                    icon: <HiOutlineVideoCamera />,
                },
                {
                    name: "Account",
                    selected: route().current("client.profile.edit"),
                    href: route("client.profile.edit"),
                    icon: <LuSettings />,
                },
                {
                    name: "Log out",
                    onClick: () => void logout(),
                    icon: <TbLogout />,
                },
            ];
        }
    }

    useEffect(() => {
        if (!transparentAtTop) return;

        const handleScroll = () => {
            setIsAtTop(window.scrollY < 50);
        };

        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [transparentAtTop]);

    return (
        <Disclosure
            as="nav"
            id="navbar"
            style={{
                backgroundColor: isImpersonating
                    ? "green"
                    : showTransparent
                      ? "transparent"
                      : (backgroundColor ?? "white"),
            }}
            className={classNames(
                "top-0 print:hidden transition-colors duration-300",
                transparentAtTop
                    ? "fixed left-0 right-0"
                    : "sticky self-stretch",
                isMinimized ? "z-40" : "z-20",
                showTransparent ? "shadow-none" : "shadow",
            )}
        >
            {({ open }) => (
                <>
                    <div
                        className={classNames(
                            "mx-auto",
                            isMinimized ? "pl-3 pr-8" : "px-6 sm:px-8",
                        )}
                    >
                        <div
                            data-testid="navbar-content"
                            className={classNames(
                                "relative flex items-center justify-between transition-all duration-300",
                                isMinimized ? "h-8" : "h-16",
                            )}
                        >
                            <div className="absolute inset-y-0 right-0 flex items-center gap-2 lg:hidden">
                                {!isSimpleNavbar && navigation.length > 0 && (
                                    <Disclosure.Button
                                        className={classNames(
                                            "relative inline-flex items-center justify-center rounded-md p-2 transition-colors duration-300",
                                            showTransparent
                                                ? "text-brand-white"
                                                : "text-brand-black",
                                        )}
                                    >
                                        <span
                                            data-testid="mobile-nav-bar"
                                            className="absolute -inset-0.5"
                                        />
                                        <span className="sr-only">
                                            Open main menu
                                        </span>
                                        {open ? (
                                            <XMarkIcon
                                                className="block h-6 w-6"
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            <Bars3Icon
                                                className="block h-6 w-6"
                                                aria-hidden="true"
                                            />
                                        )}
                                    </Disclosure.Button>
                                )}
                                {isSimpleNavbar && !user && (
                                    <button
                                        onClick={() =>
                                            void navigateTo(
                                                route("client.login"),
                                            )
                                        }
                                        className={classNames(
                                            "text-md active:bg-brand-primaryButtonClick cursor-pointer rounded-md px-3 py-2 font-medium hover:bg-brand-red hover:text-brand-white transition-colors duration-300",
                                            showTransparent
                                                ? "text-brand-white"
                                                : "text-brand-black",
                                        )}
                                    >
                                        Log in
                                    </button>
                                )}
                            </div>
                            <div className="flex flex-1 items-center justify-start sm:items-stretch sm:justify-between">
                                <div className="flex shrink-0 items-center">
                                    {isMinimized ? (
                                        <img
                                            data-testid="navbar-favicon"
                                            className="h-6 w-6"
                                            src={`${import.meta.env.VITE_CDN_URL}/images/favicon.webp`}
                                            alt="Strawberry"
                                        />
                                    ) : (
                                        <ApplicationLogo
                                            useDark={!showTransparent}
                                            useStrawberry={useStrawberryLogo}
                                            allowClick={
                                                !LOGO_CLICK_DISALLOWED_ROUTES.includes(
                                                    route().current() ?? "",
                                                )
                                            }
                                        />
                                    )}
                                </div>
                                <div
                                    data-testid="nav-bar-section"
                                    className="hidden sm:ml-6 lg:block"
                                >
                                    <div className="flex space-x-4">
                                        {!isSimpleNavbar
                                            ? navigation.map((item) =>
                                                  "customComponent" in item ? (
                                                      item.customComponent
                                                  ) : (
                                                      <Link
                                                          key={item.name}
                                                          href={item.href}
                                                          className={classNames(
                                                              "flex items-center rounded-md px-3 py-2 text-sm font-medium hover:bg-brand-red hover:text-brand-white active:bg-brand-primaryButtonClick transition-colors duration-300",
                                                              showTransparent
                                                                  ? "text-brand-white"
                                                                  : "text-brand-black",
                                                          )}
                                                          aria-current={
                                                              item.current
                                                                  ? "page"
                                                                  : undefined
                                                          }
                                                      >
                                                          {item.name}
                                                      </Link>
                                                  ),
                                              )
                                            : !user && (
                                                  <button
                                                      onClick={() =>
                                                          void navigateTo(
                                                              route(
                                                                  "client.login",
                                                              ),
                                                          )
                                                      }
                                                      className={classNames(
                                                          "text-md cursor-pointer rounded-md px-3 py-2 font-medium hover:bg-brand-red hover:text-brand-white active:bg-brand-primaryButtonClick transition-colors duration-300",
                                                          showTransparent
                                                              ? "text-brand-white"
                                                              : "text-brand-black",
                                                      )}
                                                  >
                                                      Log in
                                                  </button>
                                              )}
                                    </div>
                                </div>
                            </div>
                            {user && (
                                <div className="absolute inset-y-0 right-0 ml-3 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                                    <ProfileIcon
                                        user={user}
                                        profileMenuItems={profileMenuItems}
                                        menuItemsClassNames="-right-4"
                                        nameClassNames={classNames(
                                            "transition-all duration-300",
                                            isMinimized
                                                ? "text-sm"
                                                : "text-base",
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <Disclosure.Panel
                        data-testid="mobile-nav-bar-section"
                        className="absolute w-full shadow-lg lg:hidden"
                        style={{
                            backgroundColor: backgroundColor ?? "white",
                        }}
                    >
                        <div className="space-y-1 px-2 pb-4 pt-2">
                            {navigation.map((item) =>
                                "customComponent" in item ? (
                                    item.customComponent
                                ) : (
                                    <Link
                                        key={item.name}
                                        href={item.href}
                                        className="block rounded-md px-3 py-2 text-base font-medium text-brand-black"
                                    >
                                        {item.name}
                                    </Link>
                                ),
                            )}
                        </div>
                    </Disclosure.Panel>
                </>
            )}
        </Disclosure>
    );
}
